/**
 * Pure Gantt layout primitives (Phase 12 / M10).
 *
 * Given a list of cards + a viewport scale, produce concrete pixel
 * positions for bars, dependency arrows, and time-scale ticks. All
 * helpers are pure — no React state — so they're snapshot-testable.
 *
 * Date defaults: a card without due_date renders as a 7-day bar
 * starting at created_at (matches the original chaos-ui-components
 * behaviour the founder approved in design review).
 */
import type { Card } from '@chaos/types/kanban';
import type { CardDependency, GanttScale } from '@chaos/types/views';

const DAY_MS = 24 * 60 * 60 * 1000;

const SCALE_PX_PER_DAY: Record<GanttScale, number> = {
  day: 80,
  week: 24,
  month: 6,
};

const SCALE_TICK_DAYS: Record<GanttScale, number> = {
  day: 1,
  week: 7,
  month: 30,
};

export interface GanttBarLayout {
  card: Card;
  startISO: string;
  endISO: string;
  x: number;
  width: number;
  row: number;
}

export interface GanttArrow {
  fromCardId: string;
  toCardId: string;
  d: string;
}

export interface GanttScaleTick {
  iso: string;
  x: number;
  label: string;
}

export interface GanttLayout {
  bars: GanttBarLayout[];
  arrows: GanttArrow[];
  ticks: GanttScaleTick[];
  width: number;
  height: number;
  rangeStart: Date;
  rangeEnd: Date;
}

export interface ComputeGanttLayoutOptions {
  cards: readonly Card[];
  dependencies?: readonly CardDependency[];
  scale: GanttScale;
  rowHeight: number;
  padDays?: number;
  now?: Date;
}

export function computeGanttLayout(opts: ComputeGanttLayoutOptions): GanttLayout {
  const { cards, scale, rowHeight, padDays = 2, dependencies = [], now } = opts;

  if (cards.length === 0) {
    const today = now ?? new Date();
    return {
      bars: [],
      arrows: [],
      ticks: [],
      width: 0,
      height: rowHeight,
      rangeStart: today,
      rangeEnd: today,
    };
  }

  const ranges = cards.map((card) => cardRange(card, now));
  let minStart = ranges[0]?.start ?? new Date();
  let maxEnd = ranges[0]?.end ?? new Date();
  for (const r of ranges) {
    if (r.start.getTime() < minStart.getTime()) minStart = r.start;
    if (r.end.getTime() > maxEnd.getTime()) maxEnd = r.end;
  }

  const rangeStart = addDays(minStart, -padDays);
  const rangeEnd = addDays(maxEnd, padDays);
  const pxPerDay = SCALE_PX_PER_DAY[scale];
  const totalDays = Math.max(1, daysBetween(rangeStart, rangeEnd));
  const width = totalDays * pxPerDay;

  const bars: GanttBarLayout[] = cards.map((card, i) => {
    const range = ranges[i] ?? cardRange(card, now);
    const xStartDays = daysBetween(rangeStart, range.start);
    const widthDays = Math.max(1, daysBetween(range.start, range.end));
    return {
      card,
      startISO: range.start.toISOString(),
      endISO: range.end.toISOString(),
      x: xStartDays * pxPerDay,
      width: widthDays * pxPerDay,
      row: i,
    };
  });

  const ticks = buildTicks(rangeStart, rangeEnd, scale, pxPerDay);
  const arrows = buildArrows(bars, dependencies, rowHeight);

  return {
    bars,
    arrows,
    ticks,
    width,
    height: bars.length * rowHeight,
    rangeStart,
    rangeEnd,
  };
}

/**
 * Critical path = longest chain of dependent cards (by sum of days).
 * Uses simple DAG-DFS from each root; bounded by the dependency graph
 * size we expect (a few hundred edges max).
 */
export function computeCriticalPath(
  cards: readonly Card[],
  dependencies: readonly CardDependency[],
): Set<string> {
  if (cards.length === 0 || dependencies.length === 0) return new Set();
  const cardById = new Map(cards.map((c) => [c.id, c]));
  const childMap = new Map<string, string[]>();
  for (const link of dependencies) {
    // ``dependsOnCardId`` is the predecessor — edge points predecessor → card.
    const arr = childMap.get(link.dependsOnCardId);
    if (arr) arr.push(link.cardId);
    else childMap.set(link.dependsOnCardId, [link.cardId]);
  }
  const childSet = new Set(dependencies.map((l) => l.cardId));
  const roots = cards.filter((c) => !childSet.has(c.id));

  let bestPath: string[] = [];
  let bestDays = 0;
  function walk(cardId: string, path: string[], lengthDays: number) {
    const card = cardById.get(cardId);
    if (!card) return;
    const range = cardRange(card);
    const myDays = daysBetween(range.start, range.end);
    const total = lengthDays + myDays;
    const newPath = [...path, cardId];
    const children = childMap.get(cardId) ?? [];
    if (children.length === 0) {
      if (total > bestDays) {
        bestDays = total;
        bestPath = newPath;
      }
      return;
    }
    for (const child of children) walk(child, newPath, total);
  }
  for (const root of roots) walk(root.id, [], 0);
  return new Set(bestPath);
}

export function pxPerDay(scale: GanttScale): number {
  return SCALE_PX_PER_DAY[scale];
}

export function cardRange(card: Card, now?: Date): { start: Date; end: Date } {
  const today = now ?? new Date();
  const created = card.createdAt ? new Date(card.createdAt) : today;
  if (card.dueDate) {
    const due = new Date(card.dueDate);
    return { start: created, end: due > created ? due : addDays(created, 1) };
  }
  return { start: created, end: addDays(created, 7) };
}

export function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setUTCDate(out.getUTCDate() + n);
  return out;
}

export function daysBetween(a: Date, b: Date): number {
  return Math.max(0, Math.round((b.getTime() - a.getTime()) / DAY_MS));
}

function buildTicks(start: Date, end: Date, scale: GanttScale, px: number): GanttScaleTick[] {
  const tickDays = SCALE_TICK_DAYS[scale];
  const total = daysBetween(start, end);
  const out: GanttScaleTick[] = [];
  for (let day = 0; day <= total; day += tickDays) {
    const date = addDays(start, day);
    out.push({
      iso: date.toISOString().slice(0, 10),
      x: day * px,
      label: formatTick(date, scale),
    });
  }
  return out;
}

function formatTick(date: Date, scale: GanttScale): string {
  if (scale === 'month') {
    return date.toLocaleDateString(undefined, { month: 'short' });
  }
  if (scale === 'week') {
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  return date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' });
}

function buildArrows(
  bars: readonly GanttBarLayout[],
  dependencies: readonly CardDependency[],
  rowHeight: number,
): GanttArrow[] {
  const barById = new Map(bars.map((b) => [b.card.id, b]));
  const out: GanttArrow[] = [];
  for (const link of dependencies) {
    const from = barById.get(link.dependsOnCardId);
    const to = barById.get(link.cardId);
    if (!from || !to) continue;
    const fromX = from.x + from.width;
    const fromY = from.row * rowHeight + rowHeight / 2;
    const toX = to.x;
    const toY = to.row * rowHeight + rowHeight / 2;
    const midX = fromX + Math.max(8, (toX - fromX) / 2);
    const d = [
      `M ${fromX.toFixed(1)} ${fromY.toFixed(1)}`,
      `L ${midX.toFixed(1)} ${fromY.toFixed(1)}`,
      `L ${midX.toFixed(1)} ${toY.toFixed(1)}`,
      `L ${toX.toFixed(1)} ${toY.toFixed(1)}`,
    ].join(' ');
    out.push({ fromCardId: link.dependsOnCardId, toCardId: link.cardId, d });
  }
  return out;
}

export const __internals_for_tests = { buildTicks, buildArrows, formatTick };
